import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Mail, Lock, User, Phone, Eye, EyeOff,
  CheckCircle, AlertCircle, Building, Loader2
} from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';

const RealtorAuthPage = ({ initialMode }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, register, verifyEmail, resendVerification, forgotPassword, resetPassword, isAuthenticated } = useAuth();

  // Determine initial view from URL or prop
  const getInitialView = () => {
    // Check for prop-based mode first (from App.js routes)
    if (initialMode === 'verify') return 'verify';
    if (initialMode === 'reset-password') return 'reset-password';

    // Fall back to URL query params (legacy support)
    const mode = searchParams.get('mode');
    const token = searchParams.get('token');
    if (mode === 'verify' && token) return 'verify';
    if (mode === 'reset' && token) return 'reset-password';
    if (mode === 'signup') return 'signup';
    if (mode === 'forgot') return 'forgot';
    return 'login';
  };

  const [view, setView] = useState(getInitialView());
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form data
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetData, setResetData] = useState({ password: '', confirmPassword: '' });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/realtor/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Handle email verification on mount
  useEffect(() => {
    const token = searchParams.get('token');
    if (view === 'verify' && token) {
      handleVerifyEmail(token);
    }
  }, [view, searchParams]);

  const handleVerifyEmail = async (token) => {
    setLoading(true);
    setError('');
    try {
      await verifyEmail(token);
      setSuccess('Email verified successfully! You can now login.');
      setTimeout(() => setView('login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(loginData.email, loginData.password);
      navigate('/realtor/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Password validation helper
  const validatePassword = (password) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasMinLength = password.length >= 8;
    return { hasUppercase, hasLowercase, hasNumber, hasMinLength, isValid: hasUppercase && hasLowercase && hasNumber && hasMinLength };
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');

    if (signupData.password !== signupData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordCheck = validatePassword(signupData.password);
    if (!passwordCheck.isValid) {
      let missing = [];
      if (!passwordCheck.hasMinLength) missing.push('at least 8 characters');
      if (!passwordCheck.hasUppercase) missing.push('1 uppercase letter (A-Z)');
      if (!passwordCheck.hasLowercase) missing.push('1 lowercase letter (a-z)');
      if (!passwordCheck.hasNumber) missing.push('1 number (0-9)');
      setError(`Password must contain: ${missing.join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      await register({
        name: signupData.name,
        email: signupData.email,
        phone: signupData.phone,
        password: signupData.password
      });
      setSuccess('Registration successful! Please check your email to verify your account.');
      setView('verification-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await forgotPassword(forgotEmail);
      setSuccess('Password reset link sent to your email.');
      setView('forgot-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (resetData.password !== resetData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (resetData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      const token = searchParams.get('token');
      await resetPassword(token, resetData.password);
      setSuccess('Password reset successfully! You can now login.');
      setTimeout(() => setView('login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    try {
      await resendVerification(signupData.email);
      setSuccess('Verification email resent!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1c] text-white">
      <style>{`
        .auth-card {
          background: linear-gradient(135deg, rgba(20, 27, 45, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .input-field {
          background: rgba(20, 27, 45, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: all 0.2s ease;
        }

        .input-field:focus {
          border-color: rgba(16, 185, 129, 0.5);
          outline: none;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        .btn-primary {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          transition: all 0.3s ease;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(16, 185, 129, 0.3);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .hero-gradient {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, transparent 50%),
                      linear-gradient(225deg, rgba(59, 130, 246, 0.1) 0%, transparent 50%);
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-[#0a0f1c]/90 backdrop-blur-md border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Home</span>
          </Link>
          <Logo />
          <Link to="/realtor" className="text-white/80 hover:text-white font-medium transition-colors text-sm">
            View Realtors
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 pb-16 px-6 min-h-screen flex items-center justify-center hero-gradient">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row gap-8 items-center">
          {/* Left Side - Message & Image (hidden on verification/reset views) */}
          {(view === 'login' || view === 'signup' || view === 'forgot') && (
            <div className="w-full lg:w-1/2 text-center lg:text-left">
              <div className="relative rounded-2xl overflow-hidden mb-6">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Team collaboration"
                  className="w-full h-64 lg:h-80 object-cover rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c] via-transparent to-transparent"></div>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                We're Building Something Special
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-4">
                Homebuyers come to us for their mortgage needs, and we believe they deserve a complete experience — including access to trusted realtors.
              </p>
              <p className="text-gray-400 leading-relaxed mb-4">
                Right now, we're growing our realtor directory to showcase professionals like you. As our platform evolves, we aim to connect you directly with homebuyers.
              </p>
              <p className="text-emerald-400 font-medium">
                Join early, establish your presence, and grow with us.
              </p>
            </div>
          )}

          {/* Right Side - Forms */}
          <div className={`w-full ${(view === 'login' || view === 'signup' || view === 'forgot') ? 'lg:w-1/2' : 'max-w-md mx-auto'}`}>
          {/* Login Form */}
          {view === 'login' && (
            <div className="auth-card rounded-2xl p-8">
              <div className="text-center mb-8">
                <Building className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Realtor Login</h1>
                <p className="text-gray-400">Welcome back! Sign in to manage your profile.</p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <p className="text-emerald-400 text-sm">{success}</p>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="input-field w-full pl-12 pr-12 py-3 rounded-xl text-white"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setView('forgot'); setError(''); setSuccess(''); }}
                    className="text-emerald-400 text-sm hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
                </button>
              </form>

              <p className="text-center mt-6 text-gray-400">
                Don't have an account?{' '}
                <button
                  onClick={() => { setView('signup'); setError(''); setSuccess(''); }}
                  className="text-emerald-400 hover:underline font-medium"
                >
                  Sign Up Now
                </button>
              </p>
            </div>
          )}

          {/* Signup Form */}
          {view === 'signup' && (
            <div className="auth-card rounded-2xl p-8">
              <div className="text-center mb-8">
                <Building className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Realtor Registration</h1>
                <p className="text-gray-400">Join our trusted network of real estate professionals</p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleSignup} className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="text"
                      value={signupData.name}
                      onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={signupData.email}
                      onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="tel"
                      value={signupData.phone}
                      onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="(123) 456-7890"
                      required
                    />
                  </div>
                </div>


                <div>
                  <label className="block text-gray-400 text-sm mb-2">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      className="input-field w-full pl-12 pr-12 py-3 rounded-xl text-white"
                      placeholder="Create a strong password"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Must have 8+ characters, uppercase, lowercase, and a number</p>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={signupData.confirmPassword}
                      onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="Re-enter password"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 mt-6"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign Up'}
                </button>
              </form>

              <p className="text-center mt-6 text-gray-400">
                Already have an account?{' '}
                <button
                  onClick={() => { setView('login'); setError(''); setSuccess(''); }}
                  className="text-emerald-400 hover:underline font-medium"
                >
                  Sign In
                </button>
              </p>
            </div>
          )}

          {/* Verification Sent */}
          {view === 'verification-sent' && (
            <div className="auth-card rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Check Your Email</h1>
              <p className="text-gray-400 mb-6">
                We've sent a verification email to <span className="text-white font-medium">{signupData.email}</span>.
                Please click the link in the email to activate your account.
              </p>
              <button
                onClick={handleResendVerification}
                disabled={loading}
                className="text-emerald-400 hover:underline flex items-center justify-center gap-2 mx-auto"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Resend verification email
              </button>
              <p className="text-gray-500 text-sm mt-6">
                <button
                  onClick={() => { setView('login'); setError(''); setSuccess(''); }}
                  className="text-emerald-400 hover:underline"
                >
                  Back to login
                </button>
              </p>
            </div>
          )}

          {/* Verifying Email */}
          {view === 'verify' && (
            <div className="auth-card rounded-2xl p-8 text-center">
              {loading ? (
                <>
                  <Loader2 className="w-12 h-12 text-emerald-400 animate-spin mx-auto mb-4" />
                  <h1 className="text-xl font-bold text-white">Verifying email...</h1>
                </>
              ) : error ? (
                <>
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                  <h1 className="text-xl font-bold text-white mb-2">Verification Failed</h1>
                  <p className="text-red-400 mb-6">{error}</p>
                  <button
                    onClick={() => { setView('login'); setError(''); }}
                    className="text-emerald-400 hover:underline"
                  >
                    Back to login
                  </button>
                </>
              ) : (
                <>
                  <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                  <h1 className="text-xl font-bold text-white mb-2">Email Verified!</h1>
                  <p className="text-gray-400">Redirecting to login...</p>
                </>
              )}
            </div>
          )}

          {/* Forgot Password */}
          {view === 'forgot' && (
            <div className="auth-card rounded-2xl p-8">
              <div className="text-center mb-8">
                <Lock className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Forgot Password?</h1>
                <p className="text-gray-400">Enter your email to receive a password reset link</p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="email@example.com"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                </button>
              </form>

              <p className="text-center mt-6 text-gray-400">
                <button
                  onClick={() => { setView('login'); setError(''); }}
                  className="text-emerald-400 hover:underline"
                >
                  Back to login
                </button>
              </p>
            </div>
          )}

          {/* Forgot Password Sent */}
          {view === 'forgot-sent' && (
            <div className="auth-card rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">Check Your Email</h1>
              <p className="text-gray-400 mb-6">
                If an account exists with that email, we've sent a password reset link.
                Please check your inbox.
              </p>
              <button
                onClick={() => { setView('login'); setError(''); setSuccess(''); }}
                className="text-emerald-400 hover:underline"
              >
                Back to login
              </button>
            </div>
          )}

          {/* Reset Password */}
          {view === 'reset-password' && (
            <div className="auth-card rounded-2xl p-8">
              <div className="text-center mb-8">
                <Lock className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-white mb-2">Reset Password</h1>
                <p className="text-gray-400">Enter a new password for your account</p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <p className="text-emerald-400 text-sm">{success}</p>
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-5">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">New Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={resetData.password}
                      onChange={(e) => setResetData({ ...resetData, password: e.target.value })}
                      className="input-field w-full pl-12 pr-12 py-3 rounded-xl text-white"
                      placeholder="Minimum 8 characters"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-sm mb-2">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={resetData.confirmPassword}
                      onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                      className="input-field w-full pl-12 pr-4 py-3 rounded-xl text-white"
                      placeholder="Re-enter password"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
                </button>
              </form>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtorAuthPage;
